/**
 * Settings › Calendar and the reconnect notice. Business logic lives in
 * `~/lib/server/calendar/connections`.
 *
 * Aliases: calendar api, calendar connection functions
 */

import { createServerFn } from "@tanstack/react-start";

import { authMiddleware } from "~/lib/auth-middleware";
import {
  calendarChoicesSchema,
  type CalendarSettings,
  type CalendarSummary,
  type SchedulerCalendarEvent,
  schedulerEventsRangeSchema,
} from "~/lib/shared/calendar";

export const getCalendarSummaryFn = createServerFn({ method: "GET" })
  .middleware([authMiddleware])
  .handler(async ({ context }): Promise<CalendarSummary> => {
    const { getCalendarSummary } =
      await import("~/lib/server/calendar/connections");
    return getCalendarSummary(context.user);
  });

export const getCalendarSettingsFn = createServerFn({ method: "GET" })
  .middleware([authMiddleware])
  .handler(async ({ context }): Promise<CalendarSettings> => {
    const { assertCalendarAvailable, getCalendarSettings } =
      await import("~/lib/server/calendar/connections");
    await assertCalendarAvailable(context.user);
    return getCalendarSettings(context.user.id);
  });

/** Starts a sign-in and answers with the page to send her to. */
export const startCalendarConnectionFn = createServerFn({ method: "POST" })
  .middleware([authMiddleware])
  .handler(async ({ context }): Promise<string> => {
    const { assertCalendarAvailable, startCalendarConnection } =
      await import("~/lib/server/calendar/connections");
    await assertCalendarAvailable(context.user);
    return startCalendarConnection(context.user.id);
  });

export const saveCalendarChoicesFn = createServerFn({ method: "POST" })
  .middleware([authMiddleware])
  .validator(calendarChoicesSchema)
  .handler(async ({ data, context }): Promise<void> => {
    const { assertCalendarAvailable, saveCalendarChoices } =
      await import("~/lib/server/calendar/connections");
    await assertCalendarAvailable(context.user);
    await saveCalendarChoices(context.user.id, data);
  });

/** Open whatever the flag says: taking back access never waits on a release. */
export const disconnectCalendarFn = createServerFn({ method: "POST" })
  .middleware([authMiddleware])
  .handler(async ({ context }): Promise<void> => {
    const { disconnectCalendar } =
      await import("~/lib/server/calendar/connections");
    await disconnectCalendar(context.user.id);
  });

/** Her own events for one day of the session scheduler; never stored. */
export const getSchedulerCalendarEventsFn = createServerFn({ method: "GET" })
  .middleware([authMiddleware])
  .validator(schedulerEventsRangeSchema)
  .handler(async ({ data, context }): Promise<SchedulerCalendarEvent[]> => {
    const { getSchedulerCalendarEvents } =
      await import("~/lib/server/calendar/scheduler-events");
    return getSchedulerCalendarEvents(context.user, {
      start: new Date(data.start),
      end: new Date(data.end),
    });
  });
